import { Box, Typography, Grid, Dialog, DialogContent, Zoom } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import {
  addFreight,
  updateFreight
} from '../../../redux/reducer/truckManagementSlices/freightSlice';
import CustomRadioButton from '../../../../../common/components/customized/customradiobutton/CustomRadioButton';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import './AddFreight.scss';

const freightSchema = yup.object().shape({
  freightNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'Freight Number must contains only alphabets or numbers')
    .typeError('Freight Number must be a string')
    .required('Enter the Freight Number'),
  unladenWeight: yup
    .number()
    .typeError('Unladen Weight must be a number')
    .required('Enter the Unladen Weight')
    .min(100, 'Unladen Weight must be at least 3 digits'),
  unladenWeightUnit: yup
    .string()
    .trim()
    .typeError('Unladen Weight Unit must be a string')
    .required('Select the Unladen Weight Unit'),
  dimensionUnit: yup
    .string()
    .typeError('Dimension Unit must be a string')
    .required('Select the Dimension Unit'),
  length: yup
    .number()
    .typeError('Length must be a number')
    .required('Enter the Length')
    .min(1, 'Length must be at least 1'),
  width: yup
    .number()
    .typeError('Width must be a number')
    .required('Enter the Width')
    .min(1, 'Width must be at least 1'),
  breadth: yup
    .number()
    .typeError('Breadth must be a number')
    .required('Enter the Breadth')
    .min(1, 'Breadth must be at least 1'),
  ownerShip: yup.string().required('Select the Ownership'),
  owner: yup.string().when('ownerShip', {
    is: 'Hired',
    then: () => yup.string().trim().required('Enter the Owner Name'),
    otherwise: () => yup.string().notRequired()
  }),
  description: yup.string().trim().typeError('Description must be a string').notRequired()
});

interface AddFreights {
  open: boolean;
  setOpen: any;
  roleType: string;
  filteredRow: any;
  setPageNo?: any;
  setPageSize?: any;
}
interface TextField {
  id: string;
  label: string;
  field:
    | 'freightNumber'
    | 'unladenWeightUnit'
    | 'unladenWeight'
    | 'dimensionUnit'
    | 'length'
    | 'width'
    | 'breadth'
    | 'ownerShip'
    | 'description';
  placeholder: string;
  inputProps: {
    maxLength?: number;
    style?: React.CSSProperties;
  };
  options?: { id: string; label: string }[];
}

const AddFreights = ({
  open,
  setOpen,
  roleType,
  filteredRow,
  setPageNo,
  setPageSize
}: AddFreights) => {
  const [isHired, setIsHired] = useState(false);
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector(state => state.AddFreight);

  const { control, handleSubmit, setValue, reset, trigger } = useForm({
    resolver: yupResolver(freightSchema)
  });

  const textField: TextField[] = [
    {
      id: '1',
      label: 'Freight Number',
      field: 'freightNumber',
      placeholder: 'Freight Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '2',
      label: 'Unladen Weight Unit',
      field: 'unladenWeightUnit',
      placeholder: 'Unladen Weight Unit',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'kg', label: 'Kg' },
        { id: 'ton', label: 'Ton' },
        { id: 'pounds', label: 'Pounds' }
      ]
    },
    {
      id: '3',
      label: 'Unladen Weight',
      field: 'unladenWeight',
      placeholder: 'Unladen Weight',
      inputProps: { maxLength: 10 }
    },
    {
      id: '4',
      label: 'Owner',
      field: 'ownerShip',
      placeholder: 'Owner',
      inputProps: { maxLength: 10 }
    }
  ];

  const dimensionFields: TextField[] = [
    {
      id: '5',
      label: 'Dimension Unit ',
      field: 'dimensionUnit',
      placeholder: 'Dimension Unit',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'm', label: 'm' },
        { id: 'ft', label: 'ft' }
      ]
    },
    {
      id: '6',
      label: 'Length',
      field: 'length',
      placeholder: 'Length',
      inputProps: { maxLength: 10 }
    },
    {
      id: '7',
      label: 'Width',
      field: 'width',
      placeholder: 'Width',
      inputProps: { maxLength: 10 }
    },
    {
      id: '8',
      label: 'Breadth',
      field: 'breadth',
      placeholder: 'Breadth',
      inputProps: { maxLength: 10 }
    },
    {
      id: '9',
      label: 'Description',
      field: 'description',
      placeholder: 'Description',
      inputProps: { maxLength: 100 }
    }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIsHired(value === 'Hired');
    setValue('ownerShip', value);

    if (value === 'Owned') {
      if (!isHired) {
        setValue('owner', 'NA');
      }
    } else if (!value) {
      trigger('owner');
    }
  };

  const onSubmit = async (data: any) => {
    if (filteredRow) {
      let updatePayload: any = {
        breadth: data.breadth,
        description: data.description,
        dimensionUnit: data.dimensionUnit,
        freightNumber: data.freightNumber,
        freightUnladenWeight: data.unladenWeight,
        freightUnladenWeightUnit: data.unladenWeightUnit,
        length: data.length,
        ownerName: !isHired ? 'NA' : data.owner,
        ownership: isHired ? 'Hired' : 'Owned',
        width: data.width
      };
      let pay: { updatePayload: any; id: any } = {
        updatePayload: updatePayload,
        id: filteredRow.id
      };
      const action1 = await dispatch(updateFreight(pay));
      if (action1.type === updateFreight.fulfilled.type) {
        setOpen(false);
        setPageNo(1);
        setPageSize(5);
      }
    } else {
      const action2 = await dispatch(
        addFreight({
          breadth: data.breadth,
          description: data.description,
          dimensionUnit: data.dimensionUnit,
          freightNumber: data.freightNumber,
          freightUnladenWeight: data.unladenWeight,
          freightUnladenWeightUnit: data.unladenWeightUnit,
          length: data.length,
          ownerName: data.owner,
          ownership: isHired ? 'Hired' : 'Owned',
          width: data.width
        })
      );
      if (action2.type === addFreight.fulfilled.type) {
        setOpen(false);
        setPageNo(1);
        setPageSize(5);
      }
    }
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  useEffect(() => {
    setValue('breadth', filteredRow?.breadth);
    setValue('description', filteredRow?.description);
    setValue('dimensionUnit', filteredRow?.dimensionUnit);
    setValue('freightNumber', filteredRow?.freightNumber);
    setValue('unladenWeight', filteredRow?.unladenWeight);
    setValue('unladenWeightUnit', filteredRow?.freightUnladenWeightUnit);
    setValue('length', filteredRow?.length);
    setValue('width', filteredRow?.width);
    setValue('ownerShip', filteredRow?.ownership);
    setValue('owner', filteredRow?.ownerName);
    setIsHired(filteredRow?.ownership === 'Hired');
  }, [filteredRow]);

  useEffect(() => {
    setValue('ownerShip', 'Owned');
  }, []);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  return (
    <Dialog
      open={open}
      maxWidth={'md'}
      fullWidth
      className='freight-dialog animate__animated animate__zoomInRight'
      BackdropProps={{ invisible: true }}
    >
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px !important'
          }}
        >
          <Box className='closeIcon icon-position d-flex' onClick={handleClose}>
            <CustomIconButton category='CloseValue' />
          </Box>
          <Typography sx={{ fontWeight: 700 }}>{roleType}</Typography>
          <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              className='animate__animated animate__zoomIn animate__fast'
              component={'form'}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid container spacing={2}>
                {textField?.map(item => (
                  <Grid
                    item
                    sm={
                      item.field === 'unladenWeightUnit' || item.field === 'unladenWeight'
                        ? 3
                        : 6
                    }
                    xs={12}
                    key={item.id}
                    sx={item.options ? { mt: 1 } : { mt: 0 }}
                  >
                    {item.options ? (
                      <CustomSelect
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        label={item.label}
                        options={item.options}
                      />
                    ) : item.field === 'ownerShip' ? (
                      <CustomRadioButton
                        formlabel='Owner'
                        name='ownerShip'
                        radiobutton1='Owned'
                        radiobutton2='Hired'
                        value1='Owned'
                        value2='Hired'
                        value={isHired ? 'Hired' : 'Owned'}
                        control={control}
                        onChangeCallback={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChange(e)
                        }
                      />
                    ) : (
                      <CustomTextField
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        label={item.label}
                        inputProps={item.inputProps}
                      />
                    )}
                  </Grid>
                ))}
                {isHired && (
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      control={control}
                      name='owner'
                      placeholder='Owner Name'
                      id='owner'
                      label='Owner Name'
                      inputProps={{ maxLength: 40 }}
                    />
                  </Grid>
                )}
              </Grid>

              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif !important",
                  fontSize: '15px !important',
                  fontWeight: '500 !important',
                  color: '#26134b',
                  marginBottom: '8px',
                  textAlign: 'center'
                }}
              >
                Dimensions
              </Typography>

              <Grid container spacing={2}>
                {dimensionFields?.map(item => (
                  <Grid
                    item
                    xs={12}
                    sm={
                      item.id === '5' ||
                      item.id === '6' ||
                      item.id === '7' ||
                      item.id === '8'
                        ? 3
                        : 12
                    }
                    key={item.id}
                    sx={item.options ? { mt: 1 } : { mt: 0 }}
                  >
                    {' '}
                    {item.options ? (
                      <CustomSelect
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        label={item.label}
                        options={item.options}
                      />
                    ) : (
                      <CustomTextField
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        label={item.label}
                        inputProps={item.inputProps}
                        isOptional={item.id === '9' && true}
                        type={item?.id === '9' ? 'address' : null}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  marginTop: '5%',
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'flex-end'
                }}
              >
                <CustomButton
                  className='saveChanges'
                  type='submit'
                  category={roleType}
                  loading={isLoading}
                />
                <CustomButton
                  className='cancel'
                  category='Cancel'
                  onClick={() => {
                    handleClose();
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddFreights;
